<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { type RouteLocationRaw, useRoute } from 'vue-router';

import { useFeatureToggleQuery } from '@/composables/useFeatureToggle';
import { getAgentHomeRouteName, getHomeRouteName } from '@/helpers/Url/Url';
import { useLocale } from '@/router/routeMeta';

const { t } = useI18n();
const route = useRoute();
const locale = useLocale();
const featureToggleQuery = useFeatureToggleQuery();

const agencyDirectoryRoute = computed<RouteLocationRaw>(() => ({
  name: getHomeRouteName(locale.value),
  params: route.params,
  query: route.query,
}));

/**
 * The agent directory in the same language, with the searched location kept:
 * the primary location lives in the path, additional ones in the `loc` query.
 */
const agentDirectoryRoute = computed<RouteLocationRaw>(() => ({
  name: getAgentHomeRouteName(locale.value),
  params: route.params.location ? { location: route.params.location } : {},
  query: {
    ...(route.query.loc ? { loc: route.query.loc } : {}),
    ...featureToggleQuery.value,
  },
}));
</script>

<template>
  <div :class="$style.searchType">
    <span :class="$style.headline">{{ t('searchTypeToggle.label') }}</span>
    <nav :class="$style.options" :aria-label="t('searchTypeToggle.label')">
      <RouterLink
        :to="agentDirectoryRoute"
        :class="$style.option"
        data-dd-action-name="SearchBar Agent Toggle"
        data-test="search-type-agent"
      >
        {{ t('searchTypeToggle.agent') }}
      </RouterLink>
      <RouterLink
        :to="agencyDirectoryRoute"
        :class="[$style.option, $style.selectedOption]"
        aria-current="page"
        data-dd-action-name="SearchBar Agency Toggle"
        data-test="search-type-agency"
      >
        {{ t('searchTypeToggle.agency') }}
      </RouterLink>
    </nav>
  </div>
</template>

<style lang="scss" module>
@use '@/assets/variables' as *;

.searchType {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.headline {
  @include label;
}

.options {
  display: inline-flex;
  margin-top: $space-75;
  overflow: hidden;
  background-color: $color-white;
  border: 1px solid $color-grey-50;
  border-radius: $border-radius-l;
}

.option {
  box-sizing: border-box;
  display: flex;

  /* Both options share the same width, whichever label is longer. */
  flex: 1 1 0;
  align-items: center;
  justify-content: center;
  min-width: $space-800;
  padding: $space-50 $space-150;
  color: $color-grey-100;
  white-space: nowrap;

  @include body-2-semi-bold;

  &:hover {
    text-decoration: none;
    background-color: $color-grey-30;
  }
}

.selectedOption {
  color: var(--theme-color-text-fill);
  background-color: var(--theme-color-primary);

  &:hover {
    background-color: var(--theme-color-primary-hover);
  }
}
</style>
